import type { CSSProperties } from 'react';
import { useMemo } from 'react';

import type {
  BannerSize,
  BannerTheme,
  Classnames,
  IconList,
  StringOrNode,
  TypoSize,
} from '@snowit/icetypes';

import { Typo } from '@snowit/ui';
import testId from '@snowit/utils/testId';

import Icon from '../../Icon';
import Markdown from '../../Typography/Markdown';

import cx from 'classnames';
import s from './Banner.module.scss';

/**
 * @deprecated Use `IBannerV2`. See the `Banner` migration guide.
 */
export interface IBanner {
  theme?: BannerTheme;
  size?: BannerSize;
  icon?: IconList;
  title?: string;
  text?: string;
  baseSize?: TypoSize;
  content?: StringOrNode;
  id?: string;
  className?: string;
  classNames?: Classnames<'wrapper' | 'icon' | 'body' | 'title' | 'text'>;
  /** Jest/Cypress test string */
  test?: string;
}

/**
 * @deprecated Use `BannerV2`.
 *
 * Migration guide:
 * - `content` → `children`
 * - `className` → `classNames.wrapper`
 * - `errorText` → `theme="error" background={false}`
 * - `warningText` → `theme="warning" background={false}`
 * - `okText` → `theme="ok" background={false}`
 * - `infoText` → `theme="neutral" background={false}`
 * - `infoBox` → `theme="neutral" background={true}`
 * - `error | info | ok | tips | warning` keep the same theme;
 *   `background` defaults to `true`
 * - Default size changes from `df` to `xl`
 * - `size="xs"` → `size="sm"`
 * - `size="sm"` → `size="xl"`
 * - `size="df"` → `size="db"`
 */

const Banner = (props: IBanner) => {
  const {
    size = 'df',
    icon,
    title,
    text,
    baseSize = 'md',
    content,
    id,
    className,
    classNames,
    test,
  } = props;

  const errorBanner = props.theme === 'error' || props.theme === 'errorText';

  const theme: { icon: IconList; bgColor: string; iconColor: string } = useMemo(() => {
    switch (props.theme) {
      case 'error':
      case 'errorText':
        return {
          icon: 'error',
          bgColor: 'var(--c-error-l)',
          iconColor: 'var(--c-error)',
        };
      case 'tips':
        return {
          icon: 'bulb',
          bgColor: 'var(--c-purple-l)',
          iconColor: 'var(--c-purple)',
        };
      case 'warning':
      case 'warningText':
        return {
          icon: 'warning',
          bgColor: 'var(--c-warning-l)',
          iconColor: 'var(--c-warning)',
        };
      case 'ok':
      case 'okText':
        return {
          icon: 'check_circle',
          bgColor: 'var(--c-ok-l)',
          iconColor: 'var(--c-ok)',
        };
      default:
        return {
          icon: 'info_circle',
          bgColor: 'var(--c-info-l)',
          iconColor: 'var(--c-info)',
        };
    }
  }, [props.theme]);

  const renderIcon = () => {
    return (
      <Icon
        className={cx(s.icon, classNames?.icon)}
        name={icon || theme.icon}
        a11y={{ hidden: true }}
      />
    );
  };

  const renderTitle = () => {
    if (!title) return null;
    return (
      <Typo
        className={cx(s.title, classNames?.title)}
        text={title}
        tag={'h2'}
        size={'df'}
        weight={'medium'}
        color={'text'}
      />
    );
  };

  const renderText = () => {
    if (!text) return null;
    return (
      <Markdown
        classNames={{ text: cx(s.text, classNames?.text) }}
        source={text}
        baseSize={baseSize}
      />
    );
  };

  const renderContent = () => {
    if (!content) return null;
    if (typeof content === 'function') return content();
    return content;
  };

  return (
    <div
      id={id}
      {...testId(test)}
      className={cx(s.banner, className, classNames?.wrapper, {
        [s[props.theme ?? 'info']]: true,
        [s[size]]: true,
      })}
      style={
        {
          '--lc-bg': theme.bgColor,
          '--lc-icon': theme.iconColor,
        } as CSSProperties
      }
      role={errorBanner ? 'alert' : 'status'}
      aria-live={errorBanner ? 'assertive' : 'polite'}
    >
      {renderIcon()}
      <div className={cx(s.body, classNames?.body)}>
        {renderTitle()}
        {renderText()}
        {renderContent()}
      </div>
    </div>
  );
};

export default Banner;
